import { useEffect, useState } from "react";
import {
  MastersService,
  StateOut,
  CountryOut,
} from "@/api";

import { useMaster } from "@/hooks/useMasters";
import { Country } from "@/framework/types/Country";
import { CountryMapper } from "../mappers/useCountryMapper";

function useAsyncData<T>(
  loader: () => Promise<T>,
  initialData: T,
  deps: React.DependencyList
) {
  const [data, setData] = useState<T>(initialData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await loader();

        if (!cancelled) {
          setData(result);
        }
      } catch (err) {
        console.error("Failed to load data:", err);

        if (!cancelled) {
          setError(err);
          setData(initialData);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, deps);

  return {
    data,
    loading,
    error,
  };
}

export function useCountries() {
  return useAsyncData<Country[]>(
    async () => {
      const response =
        await MastersService.fetchCountriesEndpointMastersCountriesGet();

      console.log("Countries API response:", response);

      return response.map((country: CountryOut) =>
        CountryMapper.fromApi(country)
      );
    },
    [],
    []
  );
}

export function useStates(countryId?: number) {
  return useAsyncData<StateOut[]>(
    async () => {
      if (!countryId || countryId <= 0) {
        return [];
      }

      const response =
        await MastersService.fetchStateEndpointMastersStatesCountryIdGet(
          countryId
        );

      console.log("States API response:", response);

      return response;
    },
    [],
    [countryId]
  );
}

export function useSEMasters() {
  const { data: employmentStatuses = [] } =
    useMaster("employment_status_master");

  const { data: roles = [] } =
    useMaster("employment_role_master");

  const { data: genders = [] } =
    useMaster("gender_master");

  const { data: states = [] } =
    useMaster("state_master");

  const { data: industries = [] } =
    useMaster("industry_master");

  const { data: domains = [] } =
    useMaster("domain_master");

  const { data: applications = [] } =
    useMaster("application_master");

  const interviewTypes = [
    { id: 1, code: "Skill Based" },
    { id: 2, code: "Job Description Based" },
  ];

  const invoiceSalesBy = [
    { id: "Venky", code: "Venkatesan Palavesam" },
    { id: "Pushpa", code: "Pushpa Lakshmanan" },
    { id: "Raji", code: "Rajyalaxmi Bolli" },
    { id: "Sridhar", code: "Shridhar" },
    { id: "Satheesh", code: "Satheesh" },
  ];

  const gstStates = [
    { id: "1", code: "Jammu And Kashmir" },
    { id: "2", code: "Himachal Pradesh" },
    { id: "3", code: "Punjab" },
    { id: "4", code: "Chandigarh" },
    { id: "5", code: "Uttarakhand" },
    { id: "6", code: "Haryana" },
    { id: "7", code: "Delhi" },
    { id: "8", code: "Rajasthan" },
    { id: "9", code: "Uttar Pradesh" },
    { id: "10", code: "Bihar" },
    { id: "11", code: "Sikkim" },
    { id: "12", code: "Arunachal Pradesh" },
    { id: "13", code: "Nagaland" },
    { id: "14", code: "Manipur" },
    { id: "15", code: "Mizoram" },
    { id: "16", code: "Tripura" },
    { id: "17", code: "Meghalaya" },
    { id: "18", code: "Assam" },
    { id: "19", code: "West Bengal" },
    { id: "20", code: "Jharkhand" },
    { id: "21", code: "Orissa" },
    { id: "22", code: "Chhattisgarh" },
    { id: "23", code: "Madhya Pradesh" },
    { id: "24", code: "Gujarat" },
    { id: "26", code: "Dadra And Nagar Haveli & Daman And Diu" },
    { id: "27", code: "Maharashtra" },
    { id: "29", code: "Karnataka" },
    { id: "30", code: "Goa" },
    { id: "31", code: "Lakshadweep" },
    { id: "32", code: "Kerala" },
    { id: "33", code: "Tamil Nadu" },
    { id: "34", code: "Puducherry" },
    { id: "35", code: "Andaman And Nicobar" },
    { id: "36", code: "Telangana" },
    { id: "37", code: "Andhra Pradesh" },
    { id: "38", code: "Ladakh" },
    { id: "97", code: "Other Territory" },
    { id: "99", code: "Other Country" },
  ]



  return {
    employmentStatuses,
    roles,
    genders,
    states,
    industries,
    domains,
    interviewTypes,
    applications,
    invoiceSalesBy,
    gstStates
  };
}